import { FitViewOptions } from "@xyflow/react";
import Icon from "app/components/shared/Icon";
import { useState, useCallback } from "react";
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import { Build } from "app/components/build/Show/lib/types";
import { BuildStates } from "app/constants/BuildStates";
import { twMerge } from "tailwind-merge";

interface Props {
  fitViewOptions?: FitViewOptions;
  toggled: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMuteToggle: (isMuted: boolean) => void;
}

const RUNNING_STATES: BuildStates[] = ["failing", "running", "started"];
function isBuildRunning(build: Build) {
  return RUNNING_STATES.includes(build.state);
}

export default function FollowBorder({ ...props }: Props) {
  const [isMuted, setIsMuted] = useState(true); // State to manage mute status
  const { build } = useBuild();

  const toggleMute = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur(); // Unfocus after clicking so tooltip hides when mousing away
      setIsMuted((prevIsMuted) => !prevIsMuted);
      props.onMuteToggle(!isMuted);
    },
    [isMuted, props.onMuteToggle],
  );

  const handleStopClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Unfocus after clicking so tooltip hides when mousing away.
      event.currentTarget.blur();
      props.onClick?.(event);
    },
    [props.onClick],
  );

  return (
    <div
      id="build-follow-border"
      className={twMerge(
        "absolute top-0 left-0 right-0 m-auto w-full h-full items-start justify-center bg-none bg-opacity-100 rounded-md border-4 pointer-events-none",
        props.toggled ? "flex" : "hidden",
        isBuildRunning(build) ? "border-purple-600" : "border-charcoal-400",
      )}
    >
      <div
        className={`notch-corner-radius relative flex w-auto items-center justify-center rounded-b-xl pt-0.5 pb-1.5 pl-3 pr-2 pointer-events-auto ${
          isBuildRunning(build)
            ? "bg-purple-600 text-purple-600"
            : "bg-charcoal-400 text-charcoal-400"
        }`}
      >
        <button
          aria-label={isMuted ? "Unmute" : "Mute"}
          data-tooltip-placement="bottom"
          onClick={toggleMute}
          className="leading-[16px]"
        >
          {isMuted ? (
            <Icon
              icon="custom/outline/speaker-muted"
              className="h-[18px] w-5 mr-2 text-purple-100"
            />
          ) : (
            <Icon icon="custom/outline/speaker-playing" className="h-[18px] w-5 mr-2 text-white" />
          )}
        </button>
        <div
          className={`${
            isBuildRunning(build) ? "font-medium" : "font-normal"
          } text-sm text-white mr-3`}
        >
          {!isBuildRunning(build) ? "Build not running" : "Follow mode"}
        </div>
        <button
          aria-label="Turn off Follow Mode"
          data-tooltip-placement="bottom"
          className={`w-6 h-6 flex items-center justify-center text-white rounded-full hover:bg-opacity-50 ${
            isBuildRunning(build) ? "bg-purple-700" : "bg-gray-800"
          }`}
          onClick={handleStopClick}
        >
          <Icon icon="cross-tiny" className="h-[18px] w-auto text-white" />
        </button>
      </div>
    </div>
  );
}
